import { compact, uniq } from "es-toolkit/array";
import type { GetUserAnalyticsKeyEventsOutput } from "@/lib/ai/tools/get-user-analytics-key-events/server";
import {
  buildDataset,
  buildFields,
  createTableOnlyProfile,
  toString,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization";
import type { VizDataset } from "@/lib/ai/visualization/data/schema";

const TABLE_ONLY_PROFILE = createTableOnlyProfile({
  datasetFamily: "ua.keyEvents",
  variant: "events",
});

function uniqueNonEmptyStrings(values: unknown[]): string[] {
  return uniq(compact(values.map((value) => toString(value))));
}

/**
 * UA key event 設定出力を table 専用 dataset へ変換する。
 */
export function buildUaKeyEventsDatasets(
  rawOutput: unknown,
  source: VizDataset["source"],
): VizDataset[] {
  // adapter 契約外の payload（null/非 object）はここで停止して誤推論を防ぐ。
  if (typeof rawOutput !== "object" || rawOutput === null) return [];
  const output = rawOutput as Partial<GetUserAnalyticsKeyEventsOutput> &
    Record<string, unknown>;

  const keyEvents = uniqueNonEmptyStrings(
    Array.isArray(output.keyEvents) ? output.keyEvents : [],
  );
  const eventNames = uniqueNonEmptyStrings(
    Array.isArray(output.eventNames) ? output.eventNames : [],
  );
  const keyEventSet = new Set(keyEvents);
  const rows = [
    ...eventNames,
    ...keyEvents.filter((eventName) => !eventNames.includes(eventName)),
  ].map((eventName) => ({
    eventName,
    eventType: keyEventSet.has(eventName) ? "key_event" : "observed_event",
    isKeyEvent: keyEventSet.has(eventName) ? "true" : "false",
  }));

  return [
    buildDataset({
      id: "ua-key-events",
      profile: TABLE_ONLY_PROFILE,
      fields: buildFields([
        { key: "eventName", role: "dim", type: "string" },
        { key: "eventType", role: "dim", type: "string" },
        { key: "isKeyEvent", role: "dim", type: "string" },
      ]),
      rows,
      source,
      defaults: { category: "eventName" },
      notes:
        "Table-only dataset for key event configuration. Chart views are intentionally disabled.",
    }),
  ];
}
